import React, { useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api, { API_BASE } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft, BookOpen, FolderOpen, Download, ExternalLink,
  FileText, Presentation, Video, Link as LinkIcon, Search, Users, GraduationCap,
} from "lucide-react";
import { fmtDate } from "@/lib/format";
import { posterUrl } from "@/lib/seminars";
import { YEAR_LABEL, courseYears, yearsLabel } from "@/lib/school";
import StudentExerciseCard from "@/components/exercises/StudentExerciseCard";

function CourseExercisesStudent({ courseId }) {
  const { data = [] } = useQuery({ queryKey: ["exercises", courseId], queryFn: async () => (await api.get(`/courses/${courseId}/exercises`)).data });
  if (!data.length) return null;
  return (
    <div className="mt-6 space-y-3" data-testid="portal-course-exercises">
      <div className="text-sm font-medium">Esercizi · {data.length}</div>
      {data.map((e) => <StudentExerciseCard key={e.id} ex={e} queryKey={["exercises", courseId]}/>)}
    </div>
  );
}

const KIND_ICON = { slide: Presentation, pdf: FileText, video: Video, link: LinkIcon, altro: FileText };
const KIND_LABEL = { slide: "Slide", pdf: "PDF", video: "Video", link: "Link", altro: "Altro" };

export default function PortalCourseDetail() {
  const { id } = useParams();
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const [q, setQ] = useState("");

  const { data: course } = useQuery({
    queryKey: ["portal-course", id],
    queryFn: async () => (await api.get(`/courses/${id}`)).data,
  });
  const { data: materials = [], isLoading: loadingMat } = useQuery({
    queryKey: ["portal-materials", id],
    queryFn: async () => (await api.get(`/courses/${id}/materials`)).data,
  });

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return materials;
    return materials.filter((m) =>
      (m.title || "").toLowerCase().includes(needle) ||
      (m.description || "").toLowerCase().includes(needle) ||
      (m.kind || "").toLowerCase().includes(needle)
    );
  }, [materials, q]);

  if (!course) return <div className="p-10 text-sm text-stone-400">Caricamento…</div>;

  const accent = course.area_color || "#1C352D";

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1100px] mx-auto" data-testid="portal-course-detail">
      <Link to="/portale/corsi" className="inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-[#1C352D] mb-4">
        <ArrowLeft size={13}/> Torna ai corsi
      </Link>

      <div className="flex items-start gap-4 mb-6">
        <div className="w-14 h-14 rounded-xl grid place-items-center shrink-0" style={{ background: `${accent}20`, color: accent }}>
          <BookOpen size={22}/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-stone-500">
            {course.area_name || "Corso"}{course.code ? ` · ${course.code}` : ""}
          </div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-[#1C352D] mt-0.5">{course.name}</h1>
          <div className="flex flex-wrap gap-2 mt-2">
            {courseYears(course).length > 0 && <Badge className="bg-[#1C352D] text-white text-[11px] rounded-full">{yearsLabel(course)}</Badge>}
            {course.is_seminar && <Badge className="bg-[#E2725B]/10 text-[#8f3e26] border border-[#E2725B]/30 text-[11px] rounded-full">{course.seminar_kind === "workshop" ? "WORKSHOP" : "SEMINARIO"}</Badge>}
            {course.cfu != null && <Badge className="bg-stone-50 text-stone-700 border border-stone-200 text-[11px] rounded-full">{course.cfu} CFU</Badge>}
            {course.hours != null && <Badge className="bg-stone-50 text-stone-700 border border-stone-200 text-[11px] rounded-full">{course.hours} ore</Badge>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6">
        <div>
          <Card className="border border-stone-200 rounded-xl shadow-none">
            <div className="p-4 border-b border-stone-100 flex items-center justify-between gap-3">
              <div className="text-sm font-medium flex items-center gap-2">
                <FolderOpen size={14}/> Materiali del corso
                <span className="text-stone-400 font-normal">· {materials.length}</span>
              </div>
              <div className="relative w-full max-w-[220px]">
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400"/>
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Cerca materiale…"
                  className="pl-8 h-8 text-xs"
                  data-testid="course-materials-search"
                />
              </div>
            </div>

            {loadingMat ? (
              <div className="p-8 text-sm text-stone-400 text-center">Caricamento…</div>
            ) : filtered.length === 0 ? (
              <div className="p-8 text-sm text-stone-500 text-center">
                {q ? `Nessun materiale per «${q}».` : "Il docente non ha ancora caricato materiali per questo corso."}
              </div>
            ) : (
              <ul className="divide-y divide-stone-100">
                {filtered.map((m) => {
                  const Icon = KIND_ICON[m.kind] || FileText;
                  const isLink = m.kind === "link" || m.url;
                  const href = isLink ? m.url : `${API_BASE}/courses/materials/${m.id}/download?auth=${token}`;
                  return (
                    <li key={m.id} data-testid={`portal-material-${m.id}`}>
                      <a href={href} target="_blank" rel="noreferrer" className="p-3 flex items-center gap-3 hover:bg-stone-50/60 transition-colors">
                        <div className="w-9 h-9 rounded-lg bg-stone-100 grid place-items-center text-[#1C352D] shrink-0">
                          <Icon size={14}/>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium truncate">{m.title}</div>
                          <div className="text-[11px] text-stone-500 flex items-center gap-2 flex-wrap mt-0.5">
                            <span>{KIND_LABEL[m.kind] || m.kind}</span>
                            <span>· {fmtDate(m.uploaded_at)}</span>
                            {m.size > 0 && <span>· {(m.size / 1024 / 1024).toFixed(1)} MB</span>}
                          </div>
                          {m.description && <div className="text-[11px] text-stone-600 mt-1 line-clamp-2">{m.description}</div>}
                        </div>
                        {isLink ? <ExternalLink size={15} className="text-stone-400"/> : <Download size={15} className="text-stone-400"/>}
                      </a>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>
          <CourseExercisesStudent courseId={id}/>
        </div>

        <aside className="space-y-4">
          {course.is_seminar && course.poster && (
            <a href={posterUrl(course)} target="_blank" rel="noreferrer" className="block" data-testid="portal-seminar-poster">
              <img src={posterUrl(course)} alt={`Locandina ${course.name}`} className="w-full aspect-[4/5] object-cover rounded-xl border border-stone-200 shadow-sm hover:shadow-md transition-shadow"/>
            </a>
          )}
          <Card className="p-4 border border-stone-200 rounded-xl shadow-none">
            <div className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center gap-1.5">
              <Users size={11}/> Docenti
            </div>
            {(course.teachers || []).length === 0 ? (
              <div className="text-xs text-stone-400 mt-2">Nessun docente assegnato</div>
            ) : (
              <ul className="mt-2 space-y-1.5">
                {course.teachers.map((t) => (
                  <li key={t.id} className="text-sm text-stone-700">{t.name}</li>
                ))}
              </ul>
            )}
          </Card>

          {course.modules && course.modules.length > 0 && (
            <Card className="p-4 border border-stone-200 rounded-xl shadow-none">
              <div className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center gap-1.5">
                <GraduationCap size={11}/> Moduli
              </div>
              <ul className="mt-2 space-y-2">
                {course.modules.map((m) => (
                  <li key={m.id} className="text-sm">
                    <div className="font-medium text-stone-800">{m.name}</div>
                    {m.teachers_names && m.teachers_names.length > 0 && (
                      <div className="text-[11px] text-stone-500">{m.teachers_names.join(", ")}</div>
                    )}
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {course.description && (
            <Card className="p-4 border border-stone-200 rounded-xl shadow-none">
              <div className="text-[10px] uppercase tracking-widest text-stone-500">Descrizione</div>
              <div className="text-sm text-stone-700 mt-2 whitespace-pre-wrap">{course.description}</div>
            </Card>
          )}
        </aside>
      </div>
    </div>
  );
}
